<template>
  <Moveable
    ref="handler"
    class="draggable-field"
    :class="{ 'is-selected': selected }"
    :style="style"
    v-bind="moveable"
    @click="$emit('select')"
    @dragStart="onGestureStart"
    @drag="handleDrag"
    @dragEnd="handleDragEnd"
    @resizeStart="onGestureStart"
    @resize="handleResize"
    @resizeEnd="handleResizeEnd"
  >
    <FieldBox
      :type="field.type"
      :label="field.label"
      :required="field.required"
      :placeholder="field.placeholder"
      :box-height="live.height"
      :font-size-px="previewFontSizePx"
      :alignment="previewAlignment"
      :selected="selected"
      :closable="closable"
      @close="$emit('delete')"
    />
  </Moveable>
</template>

<script>

import Moveable from 'vue-moveable';
import FieldBox from '@/febos/global/pdf/formBuilder/components/FieldBox';

const SNAP_THRESHOLD = 6; // px espacio base

/**
 * Campo genérico posicionable sobre el PDF, arrastrable y redimensionable.
 *
 * SNAP MANUAL + MOVIMIENTO DIRECTO AL DOM:
 * vue-moveable captura el ciclo de pointer durante el gesto y Vue no hace flush
 * del re-render hasta soltar; por eso el movimiento se escribe DIRECTO al DOM en
 * los handlers (fluido). Para que ese transform no se borre, nada reactivo debe
 * re-renderizar el componente durante el gesto: por eso las snapLines se
 * resuelven UNA vez en dragStart (vía `snapLinesProvider`, no una prop reactiva)
 * y las líneas guía se pintan directo en el overlay (sin tocar data del editor).
 *
 * Props de geometría (`screen`): { x, y, width, height } en px base (sin zoom).
 * Emite: `select`, `delete`, `geometry-change` -> { x, y, width, height } px base.
 */
export default {
  name: 'DraggableField',
  components: {
    Moveable,
    FieldBox,
  },
  props: {
    field: { type: Object, required: true },
    screen: { type: Object, required: true },
    bounds: { type: Object, default: undefined },
    // Zoom visual del editor; los deltas de vue-moveable llegan en px pantalla.
    zoom: { type: Number, default: 1 },
    // Contenedor donde vue-moveable monta su control-box (el overlay). También
    // es donde se pintan las líneas guía.
    container: { type: null, default: undefined },
    // Función (id) -> { vertical:[], horizontal:[] } con las líneas candidatas
    // (px base). Es una referencia ESTABLE (no se recalcula por render), así que
    // pasarla como prop no re-renderiza el campo. Se invoca en dragStart.
    snapLinesProvider: { type: Function, default: null },
    selected: { type: Boolean, default: false },
    closable: { type: Boolean, default: true },
  },
  data() {
    return {
      moveable: {
        draggable: true,
        resizable: true,
        snappable: false,
        origin: false,
        throttleDrag: 0,
        throttleResize: 0,
        startDragRotate: 0,
        throttleDragRotate: 0,
        zoom: 1,
        bounds: this.bounds,
        container: this.container,
        padding: {
          top: 0, right: 0, bottom: 0, left: 0,
        },
        renderDirections: ['nw', 'ne', 'sw', 'se'],
      },
      // Geometría viva (px pantalla, espacio base). Se reconcilia con `screen`.
      live: { ...this.screen },
      // Posición snappeada pendiente de consolidar (no reactivo).
      pending: null,
      // Snap lines resueltas al iniciar el gesto (no reactivo).
      gestureSnap: { vertical: [], horizontal: [] },
      // Elementos DOM de las líneas guía (creados on-demand en el overlay).
      vLineEl: null,
      hLineEl: null,
    };
  },
  computed: {
    style() {
      const { x, y, width, height } = this.live;
      return {
        position: 'absolute',
        left: `${x}px`,
        top: `${y}px`,
        width: `${width}px`,
        height: `${height}px`,
      };
    },
    // fontSize configurado (puntos PDF) escalado a px pantalla, usando la misma
    // relación px/pt de la caja (live.height px por field.height pt). Así el
    // preview del editor refleja el tamaño real que tendrá el texto estampado.
    previewFontSizePx() {
      const sizePt = this.field.style && this.field.style.fontSize;
      if (!sizePt || !this.field.height) {
        return null;
      }
      const pxPerPt = this.live.height / this.field.height;
      return sizePt * pxPerPt;
    },
    previewAlignment() {
      return (this.field.style && this.field.style.alignment) || 'left';
    },
  },
  watch: {
    screen: {
      deep: true,
      handler(next) {
        this.live = { ...next };
        this.$nextTick(() => {
          this.resetTransform();
          this.refreshRect();
        });
      },
    },
    bounds(next) {
      this.moveable.bounds = next;
    },
    container(next) {
      this.moveable.container = next;
      this.$nextTick(() => this.refreshRect());
    },
  },
  methods: {
    onGestureStart() {
      // Resolver las líneas candidatas UNA vez (no en cada frame ni como prop
      // reactiva), con los campos tal como están al empezar el gesto.
      this.gestureSnap = this.snapLinesProvider
        ? this.snapLinesProvider(this.field.id)
        : { vertical: [], horizontal: [] };
    },
    handleDrag({ target, beforeTranslate }) {
      const nx = this.screen.x + beforeTranslate[0] / this.zoom;
      const ny = this.screen.y + beforeTranslate[1] / this.zoom;
      const { x, y, snap } = this.snapMove(nx, ny, this.live.width, this.live.height);
      this.pending = { x, y, width: this.live.width, height: this.live.height };
      const dx = (x - this.screen.x) * this.zoom;
      const dy = (y - this.screen.y) * this.zoom;
      // eslint-disable-next-line no-param-reassign
      target.style.transform = `translate(${dx}px, ${dy}px)`;
      this.drawGuides(snap);
    },
    handleDragEnd() {
      this.commitPending();
    },
    handleResize({ target, width, height, drag }) {
      const w = width / this.zoom;
      const h = height / this.zoom;
      const nx = this.screen.x + drag.beforeTranslate[0] / this.zoom;
      const ny = this.screen.y + drag.beforeTranslate[1] / this.zoom;
      const {
        x, y, width: sw, height: sh, snap,
      } = this.snapResize(nx, ny, w, h);
      this.pending = { x, y, width: sw, height: sh };
      // eslint-disable-next-line no-param-reassign
      target.style.width = `${sw * this.zoom}px`;
      // eslint-disable-next-line no-param-reassign
      target.style.height = `${sh * this.zoom}px`;
      // eslint-disable-next-line no-param-reassign
      target.style.transform = `translate(${(x - this.screen.x) * this.zoom}px, ${(y - this.screen.y) * this.zoom}px)`;
      this.drawGuides(snap);
    },
    handleResizeEnd() {
      this.commitPending();
    },
    commitPending() {
      if (this.pending) {
        this.live = { ...this.pending };
        this.pending = null;
      }
      this.clearGuides();
      this.resetTransform();
      this.emitGeometry();
      this.$nextTick(() => this.refreshRect());
    },
    // --- Snap manual ----------------------------------------------------------
    snapMove(x, y, w, h) {
      const v = this.gestureSnap.vertical || [];
      const h2 = this.gestureSnap.horizontal || [];
      const vs = nearestSnap([x, x + w / 2, x + w], v);
      const hs = nearestSnap([y, y + h / 2, y + h], h2);
      return {
        x: vs ? x + vs.delta : x,
        y: hs ? y + hs.delta : y,
        snap: { v: vs ? vs.line : null, h: hs ? hs.line : null },
      };
    },
    snapResize(x, y, w, h) {
      const v = this.gestureSnap.vertical || [];
      const h2 = this.gestureSnap.horizontal || [];
      let width = w;
      let height = h;
      let vLine = null;
      let hLine = null;
      const right = nearestSnap([x + w], v);
      if (right) {
        width = Math.max(4, w + right.delta);
        vLine = right.line;
      }
      const bottom = nearestSnap([y + h], h2);
      if (bottom) {
        height = Math.max(4, h + bottom.delta);
        hLine = bottom.line;
      }
      return {
        x, y, width, height, snap: { v: vLine, h: hLine },
      };
    },
    // --- Líneas guía (DOM directo en el overlay, sin reactividad) --------------
    drawGuides(snap) {
      const overlay = this.container;
      if (!overlay) {
        return;
      }
      // Vertical
      if (snap.v != null) {
        if (!this.vLineEl) {
          this.vLineEl = createLine('v');
          overlay.appendChild(this.vLineEl);
        }
        this.vLineEl.style.left = `${snap.v * this.zoom}px`;
        this.vLineEl.style.display = 'block';
      } else if (this.vLineEl) {
        this.vLineEl.style.display = 'none';
      }
      // Horizontal
      if (snap.h != null) {
        if (!this.hLineEl) {
          this.hLineEl = createLine('h');
          overlay.appendChild(this.hLineEl);
        }
        this.hLineEl.style.top = `${snap.h * this.zoom}px`;
        this.hLineEl.style.display = 'block';
      } else if (this.hLineEl) {
        this.hLineEl.style.display = 'none';
      }
    },
    clearGuides() {
      if (this.vLineEl) {
        this.vLineEl.remove();
        this.vLineEl = null;
      }
      if (this.hLineEl) {
        this.hLineEl.remove();
        this.hLineEl = null;
      }
    },
    resetTransform() {
      const el = this.$refs.handler && this.$refs.handler.$el;
      if (el) {
        el.style.transform = '';
      }
    },
    emitGeometry() {
      this.$emit('geometry-change', {
        x: this.live.x,
        y: this.live.y,
        width: this.live.width,
        height: this.live.height,
      });
    },
    refreshRect() {
      const handler = this.$refs.handler;
      if (handler && typeof handler.updateRect === 'function') {
        handler.updateRect();
      }
    },
  },
  beforeDestroy() {
    this.clearGuides();
  },
};

function createLine(axis) {
  const el = document.createElement('div');
  el.className = `pdf-field-editor__snapline pdf-field-editor__snapline--${axis}`;
  el.style.position = 'absolute';
  el.style.background = '#ff3b6b';
  el.style.zIndex = '5';
  el.style.pointerEvents = 'none';
  if (axis === 'v') {
    el.style.top = '0';
    el.style.bottom = '0';
    el.style.width = '1px';
  } else {
    el.style.left = '0';
    el.style.right = '0';
    el.style.height = '1px';
  }
  return el;
}

/**
 * Ajuste de snap más cercano dentro del umbral: { delta, line } o null.
 */
function nearestSnap(candidates, lines) {
  let best = null;
  candidates.forEach((c) => {
    lines.forEach((line) => {
      const delta = line - c;
      const dist = Math.abs(delta);
      if (dist <= SNAP_THRESHOLD && (!best || dist < Math.abs(best.delta))) {
        best = { delta, line };
      }
    });
  });
  return best;
}

</script>

<style lang="scss" scoped>

.draggable-field {
  position: absolute;
  z-index: 2;
  box-sizing: border-box;
}

.draggable-field.is-selected {
  z-index: 3;
}

</style>
